import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysCompanyAccount } from '../queryKeys';

import { fetchCompanyAccount } from './fetchCompanyAccount';
import { type CompanyAccountResponse } from './types';

export const getCompanyAccountQueryOptions = <TData = CompanyAccountResponse>({
  companyAccountId,
  options,
}: {
  companyAccountId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<CompanyAccountResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    enabled: Boolean(companyAccountId),
    queryFn: async () => await fetchCompanyAccount(companyAccountId!),
    queryKey: queryKeysCompanyAccount.get(companyAccountId),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for a company account with a given companyAccountId.
 *
 * @param params - The parameters for fetching the company account.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Account/operation/get_company_account_company_account__company_account_uuid__get
 */
export const useQueryCompanyAccount = (
  companyAccountId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<CompanyAccountResponse, Error, CompanyAccountResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery(
    getCompanyAccountQueryOptions<CompanyAccountResponse>({
      companyAccountId,
      options,
    }),
  );
};
